/**
 * BankBalanceCard — "What's my actual cash situation right now?"
 *
 * The user enters their current bank balance manually. The card then
 * projects the next 45 days of known bill payments and expected income
 * arrivals, showing a running balance so they can spot tight spots before
 * they happen.
 *
 * Current-month bills can be marked paid/unpaid directly from the timeline.
 * Paid bills are filtered from the projection and shown in a collapsible
 * "Paid This Month" section below.
 *
 * Budget tab = approximate monthly planning.
 * This card = exact, day-by-day cash flow reality check.
 */
import React, { useState, useMemo } from 'react';
import { fc, f0, getAllBills, getVarBudget, moVar, mk, sourcesToMonthly } from '../utils';

const DEFAULT_EPOCH_MS = new Date(2025, 0, 6).getTime();

/* ── Snapshot row helpers ──────────────────────────────────────────────────── */
function SnapRow({ label, value, col, bold, note, dot }) {
  const isNeg = value < 0;
  const display = value === 0 && note
    ? '—'
    : (isNeg ? '−' : '') + f0(Math.abs(value));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderTop: '1px solid var(--bdr)' }}>
      {dot && <div style={{ width: 7, height: 7, borderRadius: '50%', background: dot, flexShrink: 0 }} />}
      <span style={{ flex: 1, fontSize: 12, fontWeight: bold ? 700 : 400, color: 'var(--mut)' }}>{label}</span>
      {note && <span style={{ fontSize: 10, color: 'var(--mut)' }}>{note}</span>}
      <span style={{ fontSize: 12, fontWeight: bold ? 800 : 600, color: col || 'var(--txt)', flexShrink: 0 }}>{display}</span>
    </div>
  );
}

function SnapDivider({ label, value, col, note, highlight }) {
  const isNeg = value < 0;
  const display = (isNeg ? '−' : '') + f0(Math.abs(value));
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '9px 12px',
      borderTop: '2px solid var(--bdr)',
      background: highlight ? `rgba(${col === 'var(--grn)' ? '107,203,119' : col === 'var(--ylw)' ? '255,217,61' : '255,107,107'},.06)` : 'var(--surf2)',
    }}>
      <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: 'var(--txt)' }}>{label}</span>
      {note && <span style={{ fontSize: 10, color: 'var(--mut)' }}>{note}</span>}
      <span style={{ fontSize: highlight ? 16 : 13, fontWeight: 800, color: col, flexShrink: 0 }}>{display}</span>
    </div>
  );
}
const DAYS_AHEAD = 45;

/** Project upcoming income events for the next DAYS_AHEAD days. */
function projectIncome(incomeSources, fromDate) {
  const events = [];
  incomeSources.forEach((src) => {
    if (src.freq === 'annual') return;
    const amt = parseFloat(src.amt) || 0;
    if (!amt) return;

    if (src.freq === 'monthly') {
      for (let mo = 0; mo <= 2; mo++) {
        const d = new Date(fromDate.getFullYear(), fromDate.getMonth() + mo, 1);
        const diff = Math.ceil((d - fromDate) / 86400000);
        if (diff > 0 && diff <= DAYS_AHEAD)
          events.push({ date: d, amt, name: src.name, type: 'income' });
      }
    } else if (src.freq === 'semimonthly') {
      for (let mo = 0; mo <= 2; mo++) {
        [1, 15].forEach((day) => {
          const d = new Date(fromDate.getFullYear(), fromDate.getMonth() + mo, day);
          const diff = Math.ceil((d - fromDate) / 86400000);
          if (diff > 0 && diff <= DAYS_AHEAD)
            events.push({ date: d, amt, name: src.name, type: 'income' });
        });
      }
    } else {
      // weekly or biweekly
      const days = src.freq === 'weekly' ? 7 : 14;
      const anchor = src.firstPayMs || DEFAULT_EPOCH_MS;
      const fromEpoch = Math.floor((fromDate.getTime() - anchor) / 86400000);
      const curIdx = Math.floor(fromEpoch / days);
      for (let i = 1; i <= Math.ceil(DAYS_AHEAD / days) + 1; i++) {
        const payMs = anchor + (curIdx + i) * days * 86400000;
        const d = new Date(payMs);
        const diff = Math.ceil((d - fromDate) / 86400000);
        if (diff > 0 && diff <= DAYS_AHEAD)
          events.push({ date: d, amt, name: src.name, type: 'income' });
      }
    }
  });
  return events;
}

/** Project upcoming annual subscription charges within the next DAYS_AHEAD days. */
function projectAnnualSubs(subscriptions, fromDate) {
  const events = [];
  (subscriptions || [])
    .filter((s) => s.cycle === 'annual' && s.amt && s.due)
    .forEach((s) => {
      // s.due is day-of-month; s.dueMonth is 1-indexed month (if set), else use current year
      const dueMonth = (s.dueMonth !== null && s.dueMonth !== undefined ? s.dueMonth : fromDate.getMonth() + 1) - 1; // 0-indexed
      for (let yr = 0; yr <= 1; yr++) {
        const d = new Date(fromDate.getFullYear() + yr, dueMonth, s.due);
        const diff = Math.ceil((d - fromDate) / 86400000);
        if (diff >= 0 && diff <= DAYS_AHEAD)
          events.push({ date: d, amt: -s.amt, name: s.name + ' (annual)', col: s.col, type: 'bill', id: s.id + '_annual', billAmt: s.amt });
      }
    });
  return events;
}

/** Project upcoming bill payments for the next DAYS_AHEAD days. */
function projectBills(allBills, fromDate) {
  const events = [];
  allBills.forEach((bill) => {
    for (let mo = 0; mo <= 2; mo++) {
      const d = new Date(fromDate.getFullYear(), fromDate.getMonth() + mo, bill.due);
      const diff = Math.ceil((d - fromDate) / 86400000);
      if (diff >= 0 && diff <= DAYS_AHEAD)
        events.push({ date: d, amt: -bill.amt, name: bill.name, col: bill.col, type: 'bill', id: bill.id, billAmt: bill.amt });
    }
  });
  return events;
}

export function BankBalanceCard({ state, updateState }) {
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [creditDesc, setCreditDesc] = useState('');
  const [creditAmt, setCreditAmt] = useState('');

  const {
    bankBalance = 0,
    bankBalanceDate = null,
    bankCredits = [],
    cards = [],
    fixedBills = [],
    subscriptions = [],
    incomeSources = [],
    paidBills = {},
    extra = 0,
    varSpend = {},
    budgetSlider = 50,
    savContrib = 0,
  } = state;

  const creditsTotal = bankCredits.reduce((s, c) => s + (parseFloat(c.amt) || 0), 0);

  const addCredit = () => {
    const amt = parseFloat(creditAmt);
    if (!amt || amt <= 0 || !creditDesc.trim()) return;
    const id = Date.now().toString(36);
    updateState({ bankCredits: [...bankCredits, { id, desc: creditDesc.trim(), amt }] });
    setCreditDesc('');
    setCreditAmt('');
  };

  const removeCredit = (id) => {
    updateState({ bankCredits: bankCredits.filter((c) => c.id !== id) });
  };

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const curK = `${today.getFullYear()}-${today.getMonth()}`;
  const allBills = getAllBills(cards, fixedBills, subscriptions);

  // ── Mark paid / unpay ────────────────────────────────────────────────────────
  const handleMarkPaid = (billId, billAmt) => {
    const updated = { ...paidBills };
    if (!updated[curK]) updated[curK] = {};
    updated[curK] = { ...updated[curK], [billId]: billAmt || true };

    // Auto-deduct from bank balance when a bill is marked paid from the timeline
    const deductAmt = billAmt || 0;
    const bankUpd = bankBalance > 0 && deductAmt > 0
      ? { bankBalance: Math.max(0, Math.round((bankBalance - deductAmt) * 100) / 100), bankBalanceDate: todayStr }
      : {};

    const card = (cards || []).find((c) => c.id === billId);
    if (card) {
      const interest = card.bal * (card.apr / 100 / 12);
      const payment = card.min;
      const newBal = Math.max(0, Math.round((card.bal + interest - payment) * 100) / 100);
      updateState({
        paidBills: updated,
        cards: (cards || []).map((c) => (c.id === billId ? { ...c, bal: newBal } : c)),
        ...bankUpd,
      });
    } else {
      updateState({ paidBills: updated, ...bankUpd });
    }
  };


  // ── Timeline projection ──────────────────────────────────────────────────────
  const timeline = useMemo(() => {
    const incEvents = projectIncome(incomeSources, today);
    const billEvents = projectBills(allBills, today);
    const annualEvents = projectAnnualSubs(subscriptions, today);
    // Extra debt payment — show on the 1st of each month if set
    const extraEvents = [];
    if (extra > 0 && cards.some((c) => c.bal > 0)) {
      for (let mo = 0; mo <= 2; mo++) {
        const d = new Date(today.getFullYear(), today.getMonth() + mo, 1);
        const diff = Math.ceil((d - today) / 86400000);
        if (diff >= 0 && diff <= DAYS_AHEAD)
          extraEvents.push({ date: d, amt: -extra, name: 'Extra Debt Payment', col: 'var(--red)', type: 'bill', id: 'extra_debt_' + mo, billAmt: extra });
      }
    }
    // Planned credits (savings transfers, reimbursements, etc.) show as immediate inflows at today
    const creditEvents = bankCredits
      .filter((c) => parseFloat(c.amt) > 0)
      .map((c) => ({ date: today, amt: parseFloat(c.amt), name: c.desc, type: 'credit' }));

    const combined = [...creditEvents, ...incEvents, ...billEvents, ...annualEvents, ...extraEvents].sort((a, b) => a.date - b.date);
    const paid = paidBills[curK] || {};
    let running = bankBalance;
    return combined
      .filter((e) => !(e.type === 'bill' && e.date.getMonth() === today.getMonth() && e.date.getFullYear() === today.getFullYear() && paid[e.id]))
      .map((e) => {
        running = Math.round((running + e.amt) * 100) / 100;
        return { ...e, running };
      });
  }, [bankBalance, incomeSources, allBills, paidBills, bankCredits]); // eslint-disable-line react-hooks/exhaustive-deps

  const lowestBalance = timeline.length ? Math.min(...timeline.map((e) => e.running)) : bankBalance;
  const willGoNegative = lowestBalance < 0;
  const isTight = !willGoNegative && lowestBalance < 200;

  const daysSinceUpdate = bankBalanceDate
    ? Math.floor((today - new Date(bankBalanceDate + 'T12:00:00')) / 86400000)
    : null;
  const isStale = bankBalance > 0 && daysSinceUpdate !== null && daysSinceUpdate >= 3;

  const saveBalance = () => {
    const v = parseFloat(inputVal);
    if (isNaN(v) || v < 0) return;
    updateState({ bankBalance: Math.round(v * 100) / 100, bankBalanceDate: todayStr });
    setEditing(false);
    setInputVal('');
  };

  // ── Month snapshot: bank ↔ budget reconciliation ────────────────────────────
  const monthSnap = useMemo(() => {
    if (bankBalance <= 0) return null;

    const paid = paidBills[curK] || {};
    // Bills still unpaid this month — these are hard commitments coming out of the bank
    const unpaidThisMonth = allBills.filter((b) => !paid[b.id]);
    const unpaidTotal = unpaidThisMonth.reduce((s, b) => s + b.amt, 0);
    const afterBills = Math.round((bankBalance + creditsTotal - unpaidTotal) * 100) / 100;

    // Variable budget remaining — what's still planned to spend this month
    const monthlyIncome = sourcesToMonthly(incomeSources) || (state.monthlyIncome || 0);
    const { budget: varBudget } = getVarBudget(monthlyIncome, allBills, savContrib, budgetSlider);
    const moKey = mk(today.getFullYear(), today.getMonth());
    const spentVar = moVar(varSpend, moKey);
    const varRemaining = Math.max(0, varBudget - spentVar);

    const endOfMonth = Math.round((afterBills - varRemaining) * 100) / 100;

    return { unpaidBills: unpaidThisMonth, unpaidTotal, afterBills, varBudget, spentVar, varRemaining, endOfMonth };
  }, [bankBalance, paidBills, allBills, incomeSources, varSpend, budgetSlider, savContrib, bankCredits]); // eslint-disable-line react-hooks/exhaustive-deps

  const balColor = bankBalance <= 0 ? 'var(--red)' : isTight || willGoNegative ? 'var(--ylw)' : 'var(--grn)';

  return (
    <div className="card cp" style={{ marginBottom: 12, borderLeft: `3px solid ${balColor}` }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
            🏦 Bank Balance
          </div>
          {bankBalanceDate && (
            <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 1 }}>
              Updated {new Date(bankBalanceDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
          )}
        </div>
        <button
          onClick={() => { setEditing((e) => !e); setInputVal(bankBalance > 0 ? String(bankBalance) : ''); }}
          style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, border: '1.5px solid var(--bdr)', background: 'var(--surf2)', color: 'var(--mut)', cursor: 'pointer' }}
        >
          {editing ? 'Cancel' : '✏️ Update'}
        </button>
      </div>

      {/* Balance display / edit */}
      {editing ? (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--surf2)', border: '2px solid var(--ylw)', borderRadius: 9, padding: '6px 12px', flex: 1 }}>
            <span style={{ fontSize: 14, color: 'var(--mut)', fontWeight: 700 }}>$</span>
            <input
              type="number" min="0" step="0.01" placeholder="0.00"
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && saveBalance()}
              autoFocus
              style={{ flex: 1, background: 'none', border: 'none', fontSize: 20, fontWeight: 700, color: 'var(--ylw)', fontFamily: 'inherit' }}
            />
          </div>
          <button onClick={saveBalance} style={{ padding: '8px 16px', borderRadius: 9, background: 'var(--ylw)', color: '#000', fontWeight: 800, fontSize: 13, border: 'none', cursor: 'pointer' }}>
            Save
          </button>
        </div>
      ) : (
        <div style={{ fontFamily: 'Syne,sans-serif', fontSize: 36, fontWeight: 800, color: balColor, marginBottom: 10, lineHeight: 1 }}>
          {bankBalance > 0 ? f0(bankBalance) : bankBalance === 0 ? <span style={{ color: 'var(--mut)', fontSize: 14 }}>Tap Update to enter your balance</span> : '−' + f0(Math.abs(bankBalance))}
        </div>
      )}

      {/* Stale balance warning */}
      {isStale && !editing && (
        <div style={{ padding: '7px 12px', borderRadius: 8, background: 'rgba(255,217,61,.08)', border: '1px solid rgba(255,217,61,.2)', fontSize: 11, color: 'var(--ylw)', fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>⏱ Updated {daysSinceUpdate} day{daysSinceUpdate !== 1 ? 's' : ''} ago — tap Update to refresh</span>
        </div>
      )}

      {/* Alert banners */}
      {bankBalance > 0 && willGoNegative && (
        <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(255,107,107,.1)', border: '1px solid rgba(255,107,107,.3)', fontSize: 12, color: 'var(--red)', fontWeight: 600, marginBottom: 10 }}>
          ⚠️ Balance could go negative — lowest projected: {f0(lowestBalance)}
        </div>
      )}
      {bankBalance > 0 && isTight && (
        <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(255,217,61,.08)', border: '1px solid rgba(255,217,61,.25)', fontSize: 12, color: 'var(--ylw)', fontWeight: 600, marginBottom: 10 }}>
          ⚠️ Balance will get tight — lowest projected: {f0(lowestBalance)}
        </div>
      )}

      {/* ── Month Snapshot ──────────────────────────────────────────────────── */}
      {monthSnap && (
        <div style={{ marginBottom: 14, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--bdr)' }}>
          <div style={{ padding: '8px 12px', background: 'var(--surf2)', fontSize: 10, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
            📊 This Month at a Glance
          </div>

          {/* Row: bank balance */}
          <SnapRow label="Bank balance" value={bankBalance} col="var(--txt)" bold />

          {/* Planned credits */}
          {bankCredits.map((c) => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderTop: '1px solid var(--bdr)' }}>
              <span style={{ flex: 1, fontSize: 12, color: 'var(--grn)' }}>+ {c.desc}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--grn)', flexShrink: 0 }}>+{f0(parseFloat(c.amt))}</span>
              <button onClick={() => removeCredit(c.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--mut)', fontSize: 14, padding: '0 2px', lineHeight: 1 }}>✕</button>
            </div>
          ))}

          {/* Unpaid bills list */}
          {monthSnap.unpaidBills.length > 0 ? (
            monthSnap.unpaidBills.map((b) => (
              <SnapRow key={b.id} label={`− ${b.name}`} value={-b.amt} col="var(--red)" dot={b.col} />
            ))
          ) : (
            <SnapRow label="− Bills this month" value={0} col="var(--grn)" note="all paid ✓" />
          )}

          {/* Divider: after bills */}
          <SnapDivider
            label="After bills"
            value={monthSnap.afterBills}
            col={monthSnap.afterBills < 0 ? 'var(--red)' : monthSnap.afterBills < 300 ? 'var(--ylw)' : 'var(--grn)'}
            note={monthSnap.unpaidBills.length > 0 ? `${monthSnap.unpaidBills.length} bill${monthSnap.unpaidBills.length !== 1 ? 's' : ''} left` : undefined}
          />

          {/* Variable budget remaining */}
          {monthSnap.varBudget > 0 && (
            <SnapRow
              label={`− Variable budget left`}
              value={-monthSnap.varRemaining}
              col="var(--pnk)"
              note={`${f0(monthSnap.spentVar)} of ${f0(monthSnap.varBudget)} spent`}
            />
          )}

          {/* Divider: end of month estimate */}
          {monthSnap.varBudget > 0 && (
            <SnapDivider
              label="End of month estimate"
              value={monthSnap.endOfMonth}
              col={monthSnap.endOfMonth < 0 ? 'var(--red)' : monthSnap.endOfMonth < 200 ? 'var(--ylw)' : 'var(--grn)'}
              highlight
            />
          )}
        </div>
      )}

      {/* Planned Credits add form — shown whenever bank balance is set */}
      {bankBalance > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>
            + Planned Credits
          </div>
          <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 8 }}>
            Add expected inflows not yet in your balance — savings transfers, reimbursements, etc.
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Description (e.g. savings transfer)"
              value={creditDesc}
              onChange={(e) => setCreditDesc(e.target.value)}
              style={{ flex: 2, padding: '6px 10px', borderRadius: 7, border: '1px solid var(--bdr)', background: 'var(--surf2)', color: 'var(--txt)', fontSize: 12, fontFamily: 'inherit' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, background: 'var(--surf2)', border: '1px solid var(--bdr)', borderRadius: 7, padding: '6px 8px', flex: 1 }}>
              <span style={{ fontSize: 12, color: 'var(--mut)' }}>$</span>
              <input
                type="number" min="0" step="0.01" placeholder="0"
                value={creditAmt}
                onChange={(e) => setCreditAmt(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addCredit()}
                style={{ flex: 1, background: 'none', border: 'none', fontSize: 12, color: 'var(--txt)', fontFamily: 'inherit', minWidth: 0 }}
              />
            </div>
            <button
              onClick={addCredit}
              style={{ padding: '6px 12px', borderRadius: 7, background: 'var(--grn)', color: '#000', fontWeight: 800, fontSize: 12, border: 'none', cursor: 'pointer', flexShrink: 0 }}
            >
              Add
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      {bankBalance > 0 && timeline.length > 0 && (
        <>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
            Next {DAYS_AHEAD} Days
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {timeline.map((e, i) => {
              const isLow = e.running < 200;
              const isNeg = e.running < 0;
              const isCurrentMoBill = e.type === 'bill' &&
                e.date.getMonth() === today.getMonth() &&
                e.date.getFullYear() === today.getFullYear();
              return (
                <div
                  key={i}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', borderBottom: '1px solid var(--bdr)', fontSize: 12 }}
                >
                  <div style={{ width: 62, flexShrink: 0, fontSize: 11, color: 'var(--mut)' }}>
                    {e.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div
                    style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: (e.type === 'income' || e.type === 'credit') ? 'var(--grn)' : (e.col || 'var(--red)'),
                    }}
                  />
                  <div style={{ flex: 1, fontWeight: 500, color: (e.type === 'income' || e.type === 'credit') ? 'var(--grn)' : 'var(--txt)' }}>
                    {e.name}{e.type === 'credit' ? ' ✦' : ''}
                  </div>
                  <div style={{ fontWeight: 700, color: (e.type === 'income' || e.type === 'credit') ? 'var(--grn)' : 'var(--red)', flexShrink: 0 }}>
                    {(e.type === 'income' || e.type === 'credit') ? '+' : '−'}{fc(Math.abs(e.amt))}
                  </div>
                  <div style={{ width: 72, textAlign: 'right', fontWeight: 700, flexShrink: 0, color: isNeg ? 'var(--red)' : isLow ? 'var(--ylw)' : 'var(--mut)', fontSize: 11 }}>
                    {e.running >= 0 ? f0(e.running) : '−' + f0(Math.abs(e.running))}
                  </div>
                  {isCurrentMoBill ? (
                    <button
                      onClick={() => handleMarkPaid(e.id, e.billAmt)}
                      title="Mark as paid"
                      style={{
                        flexShrink: 0, width: 26, height: 26, borderRadius: 6,
                        border: '1.5px solid var(--bdr)', background: 'var(--surf2)',
                        color: 'var(--mut)', fontSize: 13, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        lineHeight: 1,
                      }}
                    >
                      ✓
                    </button>
                  ) : (
                    <div style={{ flexShrink: 0, width: 26 }} />
                  )}
                </div>
              );
            })}
          </div>
          {allBills.length === 0 && incomeSources.length === 0 && (
            <div style={{ fontSize: 11, color: 'var(--mut)', marginTop: 8 }}>
              Add bills and income sources in 🗺️ Plan to see projections.
            </div>
          )}
        </>
      )}

    </div>
  );
}
